import React, { useState, useCallback, forwardRef, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform, animate } from 'framer-motion';
import {
  ArrowTrendingUpIcon,
  BoltIcon,
  CheckIcon,
  ChevronRightIcon,
  EnvelopeIcon,
  GiftIcon,
  HandRaisedIcon,
  LinkIcon,
  MapPinIcon,
  PhoneIcon,
  RocketLaunchIcon,
  SparklesIcon,
  TrophyIcon,
  UserGroupIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import { copyTextToClipboard } from '../../utils/clipboard';
import ShortActionShareModal from './ShortActionShareModal';
import DashboardPanelHeader from './DashboardPanelHeader';

const SPRING = { type: 'spring', stiffness: 380, damping: 30 };
const ENTRANCE = { ease: [0.23, 1, 0.32, 1], duration: 0.35 };

const ACTION_META = {
  share_link_today: {
    Icon: LinkIcon,
    accent: 'bg-amber-500',
    accentSoft: 'text-amber-600 ',
    dot: 'bg-amber-500',
    iconBg: 'bg-amber-100/90 ',
    rowHover: 'hover:bg-amber-50/70 ',
  },
  invite_3_today: {
    Icon: UserPlusIcon,
    accent: 'bg-orange-500',
    accentSoft: 'text-orange-600 ',
    dot: 'bg-orange-500',
    iconBg: 'bg-orange-100/90 ',
    rowHover: 'hover:bg-orange-50/70 ',
  },
  remind_contacted: {
    Icon: EnvelopeIcon,
    accent: 'bg-sky-500',
    accentSoft: 'text-sky-600 ',
    dot: 'bg-sky-500',
    iconBg: 'bg-sky-100/90 ',
    rowHover: 'hover:bg-sky-50/70 ',
  },
  remind_visited: {
    Icon: MapPinIcon,
    accent: 'bg-indigo-500',
    accentSoft: 'text-indigo-600 ',
    dot: 'bg-indigo-500',
    iconBg: 'bg-indigo-100/90 ',
    rowHover: 'hover:bg-indigo-50/70 ',
  },
  congratulate_completed: {
    Icon: TrophyIcon,
    accent: 'bg-yellow-500',
    accentSoft: 'text-yellow-600 ',
    dot: 'bg-yellow-500',
    iconBg: 'bg-yellow-100/90 ',
    rowHover: 'hover:bg-yellow-50/70 ',
  },
  welcome_affiliated: {
    Icon: HandRaisedIcon,
    accent: 'bg-lime-500',
    accentSoft: 'text-lime-600 ',
    dot: 'bg-lime-500',
    iconBg: 'bg-lime-100/90 ',
    rowHover: 'hover:bg-lime-50/70 ',
  },
  browse_rewards_today: {
    Icon: GiftIcon,
    accent: 'bg-pink-500',
    accentSoft: 'text-pink-600 ',
    dot: 'bg-pink-500',
    iconBg: 'bg-pink-100/90 ',
    rowHover: 'hover:bg-pink-50/70 ',
  },
  call_affiliated: {
    Icon: PhoneIcon,
    accent: 'bg-emerald-500',
    accentSoft: 'text-emerald-600 ',
    dot: 'bg-emerald-500',
    iconBg: 'bg-emerald-100/90 ',
    rowHover: 'hover:bg-emerald-50/70 ',
  },
  call_contacted: {
    Icon: PhoneIcon,
    accent: 'bg-cyan-500',
    accentSoft: 'text-cyan-600 ',
    dot: 'bg-cyan-500',
    iconBg: 'bg-cyan-100/90 ',
    rowHover: 'hover:bg-cyan-50/70 ',
  },
  follow_up_visited: {
    Icon: MapPinIcon,
    accent: 'bg-teal-500',
    accentSoft: 'text-teal-600 ',
    dot: 'bg-teal-500',
    iconBg: 'bg-teal-100/90 ',
    rowHover: 'hover:bg-teal-50/70 ',
  },
  follow_up_completed: {
    Icon: CheckIcon,
    accent: 'bg-green-500',
    accentSoft: 'text-green-600 ',
    dot: 'bg-green-500',
    iconBg: 'bg-green-100/90 ',
    rowHover: 'hover:bg-green-50/70 ',
  },
  create_3_prospects_today: {
    Icon: RocketLaunchIcon,
    accent: 'bg-violet-500',
    accentSoft: 'text-violet-600 ',
    dot: 'bg-violet-500',
    iconBg: 'bg-violet-100/90 ',
    rowHover: 'hover:bg-violet-50/70 ',
  },
  enroll_prospect_today: {
    Icon: UserGroupIcon,
    accent: 'bg-rose-500',
    accentSoft: 'text-rose-600 ',
    dot: 'bg-rose-500',
    iconBg: 'bg-rose-100/90 ',
    rowHover: 'hover:bg-rose-50/70 ',
  },
  advance_member_today: {
    Icon: ArrowTrendingUpIcon,
    accent: 'bg-blue-500',
    accentSoft: 'text-blue-600 ',
    dot: 'bg-blue-500',
    iconBg: 'bg-blue-100/90 ',
    rowHover: 'hover:bg-blue-50/70 ',
  },
  deliver_gift_today: {
    Icon: GiftIcon,
    accent: 'bg-orange-500',
    accentSoft: 'text-orange-600 ',
    dot: 'bg-orange-500',
    iconBg: 'bg-orange-100/90 ',
    rowHover: 'hover:bg-orange-50/70 ',
  },
  review_pending_gifts: {
    Icon: GiftIcon,
    accent: 'bg-amber-500',
    accentSoft: 'text-amber-600 ',
    dot: 'bg-amber-500',
    iconBg: 'bg-amber-100/90 ',
    rowHover: 'hover:bg-amber-50/70 ',
  },
};

const DONE_ACTION_PALETTE = {
  accent: 'bg-success-500',
  accentSoft: 'text-success-600 ',
  dot: 'bg-success-500',
  iconBg: 'bg-success-100/90 ',
  rowHover: '',
};

function getActionMeta(actionKey, isDone) {
  const base = ACTION_META[actionKey] || {
    Icon: BoltIcon,
    accent: 'bg-orange-500',
    accentSoft: 'text-orange-600 ',
    dot: 'bg-orange-500',
    iconBg: 'bg-orange-100/90 ',
    rowHover: 'hover:bg-orange-50/70 ',
  };
  if (isDone) {
    return { Icon: base.Icon, ...DONE_ACTION_PALETTE };
  }
  return base;
}

function getActionCopy(action, sa, formatMessage) {
  switch (action.action_key) {
    case 'share_link_today':
      return { title: sa.shareLinkToday, subtitle: sa.shareLinkTodayDesc };
    case 'invite_3_today':
      return {
        title: sa.invite3Today,
        subtitle: formatMessage(sa.invite3TodayDesc, {
          current: action.progress?.current ?? 0,
          target: action.progress?.target ?? 3,
        }),
      };
    case 'browse_rewards_today':
      return { title: sa.browseRewardsToday, subtitle: sa.browseRewardsTodayDesc };
    case 'remind_contacted':
      return {
        title: formatMessage(sa.remindContacted, { name: action.entity_name || '' }),
        subtitle: formatMessage(sa.remindContactedDesc, {
          vendorName: action.vendor_name || sa.vendorFallback,
        }),
      };
    case 'remind_visited':
      return {
        title: formatMessage(sa.remindVisited, { name: action.entity_name || '' }),
        subtitle: sa.remindVisitedDesc,
      };
    case 'congratulate_completed':
      return {
        title: formatMessage(sa.congratulateCompleted, { name: action.entity_name || '' }),
        subtitle: sa.congratulateCompletedDesc,
      };
    case 'welcome_affiliated':
      return {
        title: formatMessage(sa.welcomeAffiliated, { name: action.entity_name || '' }),
        subtitle: sa.welcomeAffiliatedDesc,
      };
    case 'call_affiliated':
      return {
        title: formatMessage(sa.callAffiliated, { name: action.entity_name || '' }),
        subtitle: sa.callAffiliatedDesc,
      };
    case 'call_contacted':
      return {
        title: formatMessage(sa.callContacted, { name: action.entity_name || '' }),
        subtitle: sa.callContactedDesc,
      };
    case 'follow_up_visited':
      return {
        title: formatMessage(sa.followUpVisited, { name: action.entity_name || '' }),
        subtitle: sa.followUpVisitedDesc,
      };
    case 'follow_up_completed':
      return {
        title: formatMessage(sa.followUpCompleted, { name: action.entity_name || '' }),
        subtitle: sa.followUpCompletedDesc,
      };
    case 'create_3_prospects_today':
      return {
        title: sa.create3Prospects,
        subtitle: formatMessage(sa.create3ProspectsDesc, {
          current: action.progress?.current ?? 0,
          target: action.progress?.target ?? 3,
        }),
      };
    case 'enroll_prospect_today':
      return {
        title: sa.enrollProspectToday,
        subtitle: formatMessage(sa.enrollProspectTodayDesc, {
          current: action.progress?.current ?? 0,
          target: action.progress?.target ?? 1,
        }),
      };
    case 'advance_member_today':
      return {
        title: sa.advanceMemberToday,
        subtitle: formatMessage(sa.advanceMemberTodayDesc, {
          current: action.progress?.current ?? 0,
          target: action.progress?.target ?? 1,
        }),
      };
    case 'deliver_gift_today':
      return {
        title: sa.deliverGiftToday,
        subtitle: formatMessage(sa.deliverGiftTodayDesc, {
          current: action.progress?.current ?? 0,
          target: action.progress?.target ?? 1,
        }),
      };
    case 'review_pending_gifts':
      return {
        title: sa.reviewPendingGifts,
        subtitle: formatMessage(sa.reviewPendingGiftsDesc, {
          count: action.pending_count ?? 0,
        }),
      };
    default:
      return { title: action.action_key, subtitle: '' };
  }
}

function DotProgress({ current, target, dotClass, label }) {
  return (
    <div className="mt-1 sm:mt-1.5">
      <div className="mb-1 flex items-center justify-between text-[10px] font-medium text-secondary-400">
        <span>{label}</span>
        <span className="tabular-nums">
          {current}/{target}
        </span>
      </div>
      <div className="flex items-center gap-1">
        {Array.from({ length: target }, (_, i) => (
          <motion.span
            key={i}
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ ...ENTRANCE, delay: i * 0.05 }}
            className={`h-1.5 w-1.5 rounded-full transition-colors duration-300 ${
              i < current ? dotClass : 'bg-secondary-200 '
            }`}
          />
        ))}
      </div>
    </div>
  );
}

function getActionFooter(action, sa, isDone) {
  if (isDone) return { label: sa.completed };
  switch (action.action_key) {
    case 'share_link_today':
    case 'invite_3_today':
      return { label: sa.ctaShare };
    case 'remind_contacted':
    case 'remind_visited':
    case 'congratulate_completed':
    case 'welcome_affiliated':
      return { label: action.entity_email || sa.ctaRemind };
    case 'call_affiliated':
    case 'call_contacted':
    case 'follow_up_visited':
    case 'follow_up_completed':
      return { label: action.phone_number || sa.ctaCall };
    case 'create_3_prospects_today':
    case 'enroll_prospect_today':
      return { label: sa.ctaProspects };
    case 'advance_member_today':
      return { label: sa.ctaMembers };
    case 'browse_rewards_today':
      return { label: sa.ctaRewards };
    case 'deliver_gift_today':
    case 'review_pending_gifts':
      return { label: sa.ctaPendingGifts };
    default:
      return { label: '' };
  }
}

const ActionListItem = forwardRef(function ActionListItem(
  { action, role, onAction, t, formatMessage, index, isLast },
  ref,
) {
  const sa = t.enrola.shortActions;
  const isDone = action.completed;
  const meta = getActionMeta(action.action_key, isDone);
  const { Icon } = meta;
  const { title, subtitle } = getActionCopy(action, sa, formatMessage);
  const isHighReward = action.reward_tier === 'high';
  const hasProgress = action.progress && action.progress.target > 0;
  const footer = getActionFooter(action, sa, isDone);

  return (
    <motion.li
      ref={ref}
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      transition={{ ...ENTRANCE, delay: index * 0.03 }}
      className={!isLast ? 'border-b border-secondary-100/90 ' : ''}
    >
      <button
        type="button"
        disabled={isDone}
        onClick={() => !isDone && onAction(action)}
        className={`group flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors duration-200 ${
          isDone ? 'cursor-default' : meta.rowHover
        }`}
      >
        <span className="relative flex h-8 w-8 shrink-0 items-center justify-center" aria-hidden>
          {isDone ? (
            <motion.span
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={SPRING}
              className={`flex h-5 w-5 items-center justify-center rounded-full ${meta.accent}`}
            >
              <CheckIcon className="h-2.5 w-2.5 text-white" strokeWidth={3} />
            </motion.span>
          ) : (
            <span className={`flex h-8 w-8 items-center justify-center rounded-xl ${meta.iconBg} transition-colors duration-200`}>
              <Icon className={`h-4 w-4 ${meta.accentSoft}`} strokeWidth={1.75} />
            </span>
          )}
        </span>

        <span className="min-w-0 flex-1">
          <span className="flex items-baseline justify-between gap-2">
            <span
              className={`text-sm font-medium leading-snug ${
                isDone
                  ? 'text-secondary-400 line-through decoration-secondary-300/60 '
                  : 'text-secondary-900 '
              }`}
            >
              {title}
              {isHighReward && !isDone ? (
                <span className="ml-1.5 text-[10px] font-semibold uppercase tracking-wide text-amber-500">
                  {sa.highReward}
                </span>
              ) : null}
            </span>
            {action.points > 0 && !isDone ? (
              <span className={`flex shrink-0 items-center gap-0.5 text-[11px] font-semibold tabular-nums ${meta.accentSoft}`}>
                <SparklesIcon className="h-3 w-3 opacity-80" />
                {formatMessage(sa.pointsBadge, { count: action.points })}
              </span>
            ) : null}
          </span>

          <span
            className={`mt-0.5 block line-clamp-1 text-xs leading-snug ${
              isDone ? 'text-secondary-400/70 ' : 'text-secondary-500 '
            }`}
          >
            {subtitle}
          </span>

          {hasProgress && !isDone ? (
            <DotProgress
              current={action.progress.current}
              target={action.progress.target}
              dotClass={meta.dot}
              label={sa.progressLabel}
            />
          ) : null}

          {!isDone && footer.label ? (
            <span className={`mt-0.5 block truncate text-[11px] font-medium ${meta.accentSoft}`}>
              {footer.label}
            </span>
          ) : null}
        </span>

        {!isDone ? (
          <ChevronRightIcon
            className={`h-4 w-4 shrink-0 opacity-40 transition-[color,transform,opacity] duration-200 group-hover:translate-x-0.5 group-hover:opacity-100 ${meta.accentSoft}`}
            strokeWidth={2}
          />
        ) : null}
      </button>
    </motion.li>
  );
});

const SCROLL_CLASS =
  'min-h-0 flex-1 overflow-y-auto overscroll-contain [-ms-overflow-style:none] [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-secondary-300/80 ';

function ActionList({ items, role, onAction, t, formatMessage, className = '', fill = false, constrained = false }) {
  if (!items.length) return null;

  const shellBase =
    'w-full overflow-hidden rounded-2xl border border-secondary-200/60 bg-gradient-to-b from-white/90 to-orange-50/25 shadow-soft backdrop-blur-sm   ';

  const listItems = (
    <AnimatePresence mode="popLayout">
      {items.map((action, index) => (
        <ActionListItem
          key={`${action.action_key}-${action.entity_id ?? 'daily'}-${action.completed ? 'done' : 'pending'}`}
          action={action}
          role={role}
          onAction={onAction}
          t={t}
          formatMessage={formatMessage}
          index={index}
          isLast={index === items.length - 1}
        />
      ))}
    </AnimatePresence>
  );

  if (fill && constrained) {
    return (
      <div className={`${shellBase} flex h-full min-h-0 flex-col ${className}`}>
        <div className={SCROLL_CLASS}>
          <div className="flex min-h-full flex-col">
            <ul className="shrink-0">{listItems}</ul>
            <div
              className="shrink-0 border-b border-secondary-200 "
              role="separator"
              aria-hidden
            />
            <div className="min-h-0 flex-1" aria-hidden />
          </div>
        </div>
      </div>
    );
  }

  if (fill) {
    return (
      <div className={`${shellBase} flex min-h-0 flex-1 flex-col ${className}`}>
        <div className={SCROLL_CLASS}>
          <div className="flex min-h-full flex-col">
            <ul className="shrink-0">{listItems}</ul>
            <div
              className="shrink-0 border-b border-secondary-200 "
              role="separator"
              aria-hidden
            />
            <div className="min-h-0 flex-1" aria-hidden />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${shellBase} overflow-y-auto [-ms-overflow-style:none] [scrollbar-width:thin] ${className}`}>
      <ul>{listItems}</ul>
    </div>
  );
}

function shortActionItemKey(action) {
  return `${action.action_key}-${action.entity_id ?? 'daily'}`;
}

const PEEL_REST = 26;
const PEEL_MAX = 96;
const PEEL_THRESHOLD = 62;
const FLAP_CLIP = 'polygon(0px 0px, 0px 100%, 100% 0px)';
const FLAP_EDGE = 'polygon(10px 10px, 10px calc(100% - 4px), calc(100% - 4px) 10px)';
const FLAP_ZONE = 64;
const SLIDE_MAX = 72;
const SLIDE_THRESHOLD = 40;
const SLIDE_FLY = 176;

function StickyActionNote({
  action,
  isTop,
  interactive,
  onAction,
  onNotePointerDown,
  onNotePointerMove,
  onNotePointerUp,
  onNotePointerCancel,
  onCornerTap,
  onCornerKeyDown,
  cornerLabel,
  peel,
  onSlideRegister,
  t,
  formatMessage,
}) {
  const sa = t.enrola.shortActions;
  const isDone = action.completed;
  const meta = getActionMeta(action.action_key, isDone);
  const { Icon } = meta;
  const { title, subtitle } = getActionCopy(action, sa, formatMessage);
  const isHighReward = action.reward_tier === 'high';
  const hasProgress = action.progress && action.progress.target > 0;
  const footer = getActionFooter(action, sa, isDone);

  const noteClip = useTransform(
    peel,
    (s) => `polygon(0px 0px, 100% 0px, 100% calc(100% - ${s}px), calc(100% - ${s}px) 100%, 0px 100%)`
  );
  const flapSize = useTransform(peel, (s) => s);

  const localSlide = useMotionValue(0);

  useEffect(() => {
    if (isTop) {
      onSlideRegister?.(localSlide);
      return () => {
        if (onSlideRegister) {
          onSlideRegister(null, localSlide);
        }
      };
    }
    return undefined;
  }, [isTop, onSlideRegister, localSlide]);

  return (
    <motion.div
      style={{ y: localSlide }}
      onPointerDown={isTop ? onNotePointerDown : undefined}
      onPointerMove={isTop ? onNotePointerMove : undefined}
      onPointerUp={isTop ? onNotePointerUp : undefined}
      onPointerCancel={isTop ? onNotePointerCancel : undefined}
      className="relative select-none touch-none rounded-2xl shadow-[0_4px_18px_-4px_rgba(249,115,22,0.1),0_14px_34px_-12px_rgba(15,23,42,0.18)] sm:rounded-3xl"
    >
      <div
        className="relative h-44 overflow-hidden rounded-2xl border border-secondary-200/70 bg-gradient-to-b from-white via-white to-orange-50 shadow-[inset_0_1px_0_rgba(255,255,255,0.85)] sm:h-40 sm:rounded-3xl"
        style={{ clipPath: noteClip }}
      >
        <span
          className={`absolute inset-x-0 top-0 h-1 shadow-[0_2px_6px_-1px_rgba(249,115,22,0.3)] ${meta.accent}`}
          aria-hidden
        />
        <span
          className="pointer-events-none absolute inset-0 bg-[repeating-linear-gradient(to_bottom,transparent,transparent_27px,rgba(148,163,184,0.12)_27px,rgba(148,163,184,0.12)_28px)]"
          aria-hidden
        />
        <button
          type="button"
          tabIndex={isTop ? 0 : -1}
          onClick={() => isTop && onAction(action)}
          className={`group flex h-full w-full items-center gap-3 px-4 pt-2.5 pb-6 pr-16 text-left sm:gap-4 sm:px-6 sm:pt-4 sm:pb-7 sm:pr-20 ${isTop ? meta.rowHover : ''}`}
        >
          <span
            className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl shadow-sm ring-1 ring-black/[0.04] transition-transform duration-200 group-hover:scale-[1.05] sm:h-14 sm:w-14 ${meta.iconBg}`}
          >
            <Icon className={`h-5 w-5 ${meta.accentSoft} sm:h-6 sm:w-6`} strokeWidth={1.75} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 sm:gap-x-2">
              <span className="line-clamp-2 text-[15px] font-semibold leading-snug tracking-[-0.01em] text-secondary-900 antialiased sm:text-lg sm:leading-[1.75rem]">{title}</span>
              {isHighReward && !isDone ? (
                <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-600 sm:px-2 sm:text-[10px]">{sa.highReward}</span>
              ) : null}
              {action.points > 0 && !isDone ? (
                <span className={`inline-flex items-center gap-0.5 rounded-full bg-white px-1.5 py-0.5 text-[11px] font-semibold tabular-nums shadow-sm ring-1 ring-black/[0.04] sm:px-2 sm:text-xs ${meta.accentSoft}`}>
                  <SparklesIcon className="h-3 w-3 opacity-80 sm:h-3.5 sm:w-3.5" />
                  {formatMessage(sa.pointsBadge, { count: action.points })}
                </span>
              ) : null}
            </span>
            <span className="mt-1 line-clamp-1 text-sm text-secondary-500">{subtitle}</span>
            {hasProgress && !isDone ? (
              <DotProgress
                current={action.progress.current}
                target={action.progress.target}
                dotClass={meta.dot}
                label={sa.progressLabel}
              />
            ) : null}
            {footer.label ? (
              <span className={`mt-1 flex items-center gap-0.5 text-xs font-semibold ${meta.accentSoft}`}>
                <span className="truncate">{footer.label}</span>
                <ChevronRightIcon
                  className="h-3 w-3 shrink-0 -translate-x-0.5 opacity-70 transition-transform duration-200 group-hover:translate-x-0"
                  strokeWidth={2.25}
                />
              </span>
            ) : null}
          </span>
        </button>
      </div>

      <motion.div
        onClick={interactive ? onCornerTap : undefined}
        onKeyDown={interactive ? onCornerKeyDown : undefined}
        role={interactive ? 'button' : undefined}
        tabIndex={interactive && isTop ? 0 : -1}
        aria-label={interactive ? cornerLabel : undefined}
        title={interactive ? cornerLabel : undefined}
        className={`absolute bottom-0 right-0 h-16 w-16 touch-none select-none rounded-tl-3xl transition-colors duration-200 ${
          interactive
            ? 'cursor-grab active:cursor-grabbing hover:bg-secondary-500/[0.04]'
            : 'cursor-default'
        }`}
      >
        <motion.div
          className="absolute bottom-0 right-0"
          style={{ width: flapSize, height: flapSize, filter: 'drop-shadow(-3px 3px 4px rgba(15, 23, 42, 0.28))' }}
        >
          <div
            className="h-full w-full bg-gradient-to-tl from-secondary-400/80 via-secondary-200/80 to-secondary-100"
            style={{ clipPath: FLAP_CLIP }}
          />
          <div
            className="absolute inset-0 bg-gradient-to-tl from-white/10 via-white/25 to-white/60"
            style={{ clipPath: FLAP_EDGE }}
          />
        </motion.div>
      </motion.div>
    </motion.div>
  );
}

function ShortActionBanner({ items, onAction, t, formatMessage }) {
  const sa = t.enrola.shortActions;
  const total = items.length;
  const [index, setIndex] = useState(0);
  const peel = useMotionValue(PEEL_REST);
  const peelRest = useMotionValue(PEEL_REST);
  const dragRef = useRef(null);
  const suppressClickRef = useRef(false);
  const exitDirRef = useRef(1);
  const slideCtrlRef = useRef(null);

  useEffect(() => {
    setIndex(0);
  }, [total]);

  if (!total) return null;

  const safeIndex = ((index % total) + total) % total;
  const visible = total > 1
    ? [items[safeIndex], items[(safeIndex + 1) % total]]
    : [items[safeIndex]];

  const advance = () => {
    if (total < 2) return;
    exitDirRef.current = 1;
    peel.set(PEEL_REST);
    setIndex((current) => current + 1);
  };

  const goBack = () => {
    if (total < 2) return;
    exitDirRef.current = -1;
    peel.set(PEEL_REST);
    setIndex((current) => current - 1);
  };

  const springBack = () => {
    void animate(peel, PEEL_REST, { type: 'spring', stiffness: 420, damping: 34 });
  };

  const springSlideBack = () => {
    const ctrl = slideCtrlRef.current;
    if (ctrl) {
      void animate(ctrl, 0, { type: 'spring', stiffness: 420, damping: 34 });
    }
  };

  const peelOffThenAdvance = () => {
    void animate(peel, PEEL_MAX, { duration: 0.16, ease: 'easeOut' }).then(advance);
  };

  const slideThenAdvance = () => {
    const ctrl = slideCtrlRef.current;
    if (ctrl) {
      void animate(ctrl, -SLIDE_FLY, { duration: 0.15, ease: 'easeOut' }).then(advance);
    }
  };

  const slideThenBack = () => {
    const ctrl = slideCtrlRef.current;
    if (ctrl) {
      void animate(ctrl, SLIDE_FLY, { duration: 0.15, ease: 'easeOut' }).then(goBack);
    }
  };

  const clearSuppressSoon = () => {
    setTimeout(() => {
      suppressClickRef.current = false;
    }, 0);
  };

  const handleNotePointerDown = (event) => {
    suppressClickRef.current = false;
    if (total < 2) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const inFlap = rect
      ? event.clientX >= rect.right - FLAP_ZONE && event.clientY >= rect.bottom - FLAP_ZONE
      : false;
    dragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      dragging: false,
      startPeel: PEEL_REST,
      startSlide: slideCtrlRef.current?.get() ?? 0,
      mode: inFlap ? 'peel' : 'slide',
    };
  };

  const handleNotePointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag) return;
    if (!drag.dragging) {
      if (Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) < 6) return;
      drag.dragging = true;
      drag.startX = event.clientX;
      drag.startY = event.clientY;
      drag.startPeel = peel.get();
      drag.startSlide = slideCtrlRef.current?.get() ?? 0;
      suppressClickRef.current = true;
      event.currentTarget.setPointerCapture(event.pointerId);
    }

    if (drag.mode === 'peel') {
      const alongDiagonal = (-((event.clientX - drag.startX) + (event.clientY - drag.startY))) / Math.SQRT2;
      peel.set(Math.min(PEEL_MAX, Math.max(PEEL_REST, drag.startPeel + alongDiagonal)));
    } else {
      const dy = event.clientY - drag.startY;
      const ctrl = slideCtrlRef.current;
      if (ctrl) {
        ctrl.set(Math.min(SLIDE_MAX, Math.max(-SLIDE_MAX, drag.startSlide + dy)));
      }
    }
  };

  const handleNotePointerUp = () => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag || !drag.dragging) return;
    clearSuppressSoon();
    if (drag.mode === 'peel') {
      if (peel.get() >= PEEL_THRESHOLD) {
        advance();
      } else {
        springBack();
      }
    } else {
      const ctrl = slideCtrlRef.current;
      if (ctrl && ctrl.get() <= -SLIDE_THRESHOLD) {
        slideThenAdvance();
      } else if (ctrl && ctrl.get() >= SLIDE_THRESHOLD) {
        slideThenBack();
      } else {
        springSlideBack();
      }
    }
  };

  const handleNotePointerCancel = () => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag || !drag.dragging) return;
    clearSuppressSoon();
    if (drag.mode === 'peel') {
      springBack();
    } else {
      springSlideBack();
    }
  };

  const handleNoteClick = (action) => {
    if (suppressClickRef.current) return;
    onAction(action);
  };

  const handleCornerTap = () => {
    if (suppressClickRef.current) return;
    peelOffThenAdvance();
  };

  const handleCornerKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    peelOffThenAdvance();
  };

  return (
    <div className="relative grid">
      <AnimatePresence initial={false}>
        {visible.map((action, depth) => {
          const isTop = depth === 0;
          return (
            <motion.div
              key={shortActionItemKey(action)}
              custom={exitDirRef.current}
              style={{ gridArea: '1 / 1', zIndex: isTop ? 2 : 1, transformOrigin: 'bottom right' }}
              initial={isTop ? { opacity: 0, scale: 0.97, rotate: -1 } : { opacity: 0, scale: 0.985, x: 9, y: 11 }}
              animate={
                isTop
                  ? { opacity: 1, scale: 1, rotate: 0, x: 0, y: 0 }
                  : { opacity: 1, scale: 0.985, rotate: 0.6, x: 10, y: 12 }
              }
              exit={(dir) => ({
                x: dir >= 0 ? 230 : -230,
                y: dir >= 0 ? -120 : 120,
                rotate: dir >= 0 ? 13 : -13,
                scale: 1.03,
                opacity: 0,
                transition: { duration: 0.4, ease: [0.3, 0, 0.4, 1] },
              })}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className={isTop ? 'relative' : 'pointer-events-none'}
              aria-hidden={!isTop}
            >
              <StickyActionNote
                action={action}
                isTop={isTop}
                interactive={total > 1}
                onAction={handleNoteClick}
                onNotePointerDown={handleNotePointerDown}
                onNotePointerMove={handleNotePointerMove}
                onNotePointerUp={handleNotePointerUp}
                onNotePointerCancel={handleNotePointerCancel}
                onCornerTap={handleCornerTap}
                onCornerKeyDown={handleCornerKeyDown}
                cornerLabel={sa.slideNext}
                peel={isTop ? peel : peelRest}
                onSlideRegister={
                  isTop
                    ? (mv, expected) => {
                        if (expected) {
                          if (slideCtrlRef.current === expected) slideCtrlRef.current = null;
                        } else {
                          slideCtrlRef.current = mv;
                        }
                      }
                    : undefined
                }
                t={t}
                formatMessage={formatMessage}
              />
            </motion.div>
          );
        })}
      </AnimatePresence>

      {total > 1 ? (
        <span className="pointer-events-none absolute bottom-2.5 left-4 z-10 inline-flex items-center rounded-full border border-secondary-200 bg-white px-2 py-0.5 text-[10px] font-semibold tabular-nums text-secondary-500 shadow-sm sm:left-5">
          {safeIndex + 1} / {total}
        </span>
      ) : null}
    </div>
  );
}

function ShortActionsPanel({
  actions = [],
  loading = false,
  role = 'client',
  layout = 'horizontal',
  onRefresh,
  onNavigateProspects,
  onNavigateClients,
  onNavigateRewards,
  onNavigatePendingGifts,
  onPointsEarned,
  heightConstrained = false,
  actionRunnerRef,
}) {
  const { t, formatMessage } = useLanguage();
  const sa = t.enrola.shortActions;
  const isVertical = layout === 'vertical';
  const isBanner = layout === 'banner';
  const isPairedLayout = isVertical && heightConstrained;
  const [shareModal, setShareModal] = useState({ open: false, url: '', message: '' });
  const completeApi = role === 'client' ? '/api/client/short-actions/complete' : '/api/vendor/short-actions/complete';

  const completeAction = useCallback(async (actionKey, entityId = null) => {
    try {
      const response = await axios.post(
        completeApi,
        { action_key: actionKey, entity_id: entityId },
        { withCredentials: true },
      );
      if (response.data.points_awarded > 0) {
        toast.success(formatMessage(sa.pointsEarned, { count: response.data.points_awarded }));
        onPointsEarned?.(response.data.reward_points);
      }
      onRefresh?.();
      return response.data;
    } catch (error) {
      if (!error.response?.data?.already_completed) {
        console.error('Short action complete error:', error);
      }
      return null;
    }
  }, [completeApi, formatMessage, sa.pointsEarned, onPointsEarned, onRefresh]);

  const buildReminderEmail = useCallback((action) => {
    const vendorName = action.vendor_name || sa.vendorFallback;
    const memberName = action.entity_name || '';
    const templates = sa.emailTemplates || {};

    const templateKeyByAction = {
      welcome_affiliated: 'welcomeAffiliated',
      remind_visited: 'remindVisited',
      congratulate_completed: 'congratulateCompleted',
      remind_contacted: 'remindContacted',
    };
    const templateKey = templateKeyByAction[action.action_key] || 'remindContacted';
    const template = templates[templateKey] || templates.remindContacted;
    if (!template) return `mailto:${encodeURIComponent(action.entity_email || '')}`;

    const params = { name: memberName, vendorName };
    const subject = formatMessage(template.subject, params);
    const body = formatMessage(template.body, params);
    return `mailto:${encodeURIComponent(action.entity_email || '')}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }, [formatMessage, sa.emailTemplates, sa.vendorFallback]);

  const handleAction = useCallback(async (action) => {
    switch (action.action_type) {
      case 'share_referral': {
        if (action.referral_url) {
          try {
            await copyTextToClipboard(action.referral_url);
            toast.success(sa.linkCopied);
          } catch {
            toast.error(t.enrola.copyFailed);
          }
        }
        setShareModal({
          open: true,
          url: action.referral_url || '',
          message: formatMessage(t.enrola.defaultShareFallback, { url: action.referral_url }),
        });
        if (action.action_key === 'share_link_today') {
          await completeAction(action.action_key, action.entity_id);
        }
        break;
      }
      case 'email_reminder': {
        window.location.href = buildReminderEmail(action);
        await completeAction(action.action_key, action.entity_id);
        break;
      }
      case 'copy_phone': {
        if (action.phone_number) {
          try {
            await copyTextToClipboard(action.phone_number);
            toast.success(sa.phoneCopied);
          } catch {
            toast.error(t.enrola.copyFailed);
          }
        } else {
          toast.error(sa.noPhone);
        }
        await completeAction(action.action_key, action.entity_id);
        break;
      }
      case 'navigate_prospects':
        onNavigateProspects?.();
        break;
      case 'navigate_clients':
        // Legacy API responses may still tag enroll as navigate_clients
        if (action.action_key === 'enroll_prospect_today') {
          onNavigateProspects?.();
        } else {
          onNavigateClients?.();
        }
        break;
      case 'navigate_rewards':
        onNavigateRewards?.();
        if (action.action_key === 'browse_rewards_today') {
          void completeAction(action.action_key, action.entity_id);
        }
        break;
      case 'navigate_pending_gifts':
        onNavigatePendingGifts?.();
        if (action.action_key === 'review_pending_gifts') {
          void completeAction(action.action_key, action.entity_id);
        }
        break;
      default:
        break;
    }
  }, [buildReminderEmail, completeAction, formatMessage, onNavigateClients, onNavigatePendingGifts, onNavigateProspects, onNavigateRewards, sa, t.enrola]);

  useEffect(() => {
    if (actionRunnerRef) {
      actionRunnerRef.current = handleAction;
    }
  }, [actionRunnerRef, handleAction]);

  const pendingActions = actions.filter((a) => !a.completed);
  const completedActions = actions.filter((a) => a.completed);
  const motivation = role === 'client' ? sa.motivationClient : sa.motivationVendor;

  if (loading) {
    if (isBanner) {
      return (
        <section className="h-44 animate-pulse overflow-hidden rounded-2xl border border-secondary-200/70 bg-gradient-to-b from-white to-orange-50 shadow-[0_4px_18px_-4px_rgba(249,115,22,0.1),0_14px_34px_-12px_rgba(15,23,42,0.18)] sm:h-40 sm:rounded-3xl">
          <div className="h-1 w-full bg-orange-200/70" />
        </section>
      );
    }
    return (
      <section className={`min-w-0 ${isPairedLayout ? 'flex h-full min-h-0 flex-col' : isVertical ? 'flex min-h-0 flex-1 flex-col' : 'space-y-3'}`}>
        <div className="h-7 w-36 animate-pulse rounded-lg bg-secondary-200 " />
        <div
          className={`overflow-hidden rounded-2xl border border-secondary-200/60 bg-gradient-to-b from-white/90 to-orange-50/25    ${
            isPairedLayout ? 'flex h-full min-h-0 flex-1 flex-col' : isVertical ? 'flex flex-1 flex-col' : ''
          }`}
        >
          {[1, 2].map((i) => (
            <div
              key={i}
              className={`flex animate-pulse items-center gap-3 py-2.5 ${
                i < 2 ? 'border-b border-secondary-100/90 ' : ''
              }`}
            >
              <div className="h-8 w-8 shrink-0 rounded-xl bg-secondary-200/80 " />
              <div className="flex flex-1 flex-col gap-1.5">
                <div className="h-3.5 w-2/5 rounded bg-secondary-200 " />
                <div className="h-3 w-4/5 rounded bg-secondary-100 " />
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (!actions.length) return null;

  if (isBanner) {
    return (
      <>
        {pendingActions.length > 0 ? (
          <ShortActionBanner
            items={pendingActions}
            onAction={handleAction}
            t={t}
            formatMessage={formatMessage}
          />
        ) : (
          <div className="flex h-44 items-center justify-center gap-3 rounded-2xl border border-dashed border-secondary-200/80 bg-gradient-to-b from-white to-orange-50 px-4 shadow-soft sm:h-40 sm:rounded-3xl">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-success-100 text-success-600">
              <CheckIcon className="h-4 w-4" strokeWidth={2.5} />
            </span>
            <p className="text-sm font-medium text-secondary-500">{sa.allDone}</p>
          </div>
        )}

        <ShortActionShareModal
          open={shareModal.open}
          onClose={() => setShareModal({ open: false, url: '', message: '' })}
          url={shareModal.url}
          message={shareModal.message}
        />
      </>
    );
  }

  return (
    <>
      <section className={`min-w-0 ${isPairedLayout ? 'flex h-full min-h-0 flex-col' : isVertical ? 'flex min-h-0 flex-1 flex-col' : 'space-y-3'}`}>
        <DashboardPanelHeader
          eyebrow={sa.eyebrow}
          title={sa.title}
          subtitle={motivation}
          icon={BoltIcon}
          className="overflow-visible"
        />

        <div className={isPairedLayout ? 'flex min-h-0 flex-1 flex-col overflow-hidden' : isVertical ? 'flex min-h-0 flex-1 flex-col gap-3' : 'contents'}>
          {pendingActions.length > 0 ? (
            <ActionList
              items={pendingActions}
              role={role}
              onAction={handleAction}
              t={t}
              formatMessage={formatMessage}
              fill={isVertical}
              constrained={isPairedLayout}
              className={isPairedLayout ? 'min-h-0 flex-1' : ''}
            />
          ) : (
            <div
              className={`rounded-2xl border border-dashed border-orange-200/60 bg-orange-50/20 px-4 py-4 text-center   ${
                isVertical ? 'flex flex-1 flex-col justify-center' : ''
              }`}
            >
              <p className="text-sm text-secondary-500 ">{sa.allDone}</p>
            </div>
          )}

          {completedActions.length > 0 && !isPairedLayout ? (
            <div className={isVertical ? 'shrink-0' : ''}>
              <p className="mb-1.5 text-[11px] font-medium tracking-wide text-secondary-400 ">
                {sa.completedToday}
              </p>
              <ActionList
                items={completedActions.map((a) => ({ ...a, completed: true }))}
                role={role}
                onAction={() => {}}
                t={t}
                formatMessage={formatMessage}
              />
            </div>
          ) : null}
        </div>
      </section>

      <ShortActionShareModal
        open={shareModal.open}
        onClose={() => setShareModal({ open: false, url: '', message: '' })}
        url={shareModal.url}
        message={shareModal.message}
      />
    </>
  );
}

export default ShortActionsPanel;
